// Structured reference replacement for object-variable declaration values.
// Resolves object keys plus array item access without pulling in class transforms.
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import { parseVarReferenceAt } from '../class-variable/transform/scanner'
import { getObjectVariableValue } from './keys'

type ParseObjectValue = (value: string, objectVariables: LuisObjectVariables) => Record<string, string>

// Replace structured object/array references inside declaration text.
export function replaceObjectValueReferences(
  declaration: string,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  parseObjectValue: ParseObjectValue,
  depth = 0,
): string {
  let output = ''
  // Scan declaration text once and replace structured var references.
  for (let index = 0; index < declaration.length; index += 1) {
    const reference = parseVarReferenceAt(declaration, index)
    // Keep raw characters when no structured var reference starts here.
    if (!reference) {
      output += declaration[index]
      continue
    } else {
      // Continue structured reference replacement for parsed var reference.
    }

    const resolved = resolveObjectValueReference(
      reference.variableName,
      reference.objectKey,
      reference.arrayIndex,
      objectVariables,
      arrayVariables,
      parseObjectValue,
    )
    output += resolveNestedReferenceValue(resolved, objectVariables, arrayVariables, parseObjectValue, depth) ?? reference.raw
    index = reference.end - 1
  }

  // Return declaration text with structured references resolved.
  return output
}

// Resolve object-key and array-item references used by object values.
function resolveObjectValueReference(
  objectName: string,
  objectKey: string | null,
  arrayIndex: number | null,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  parseObjectValue: ParseObjectValue,
): string | undefined {
  // Resolve array item access before object-key fallback.
  if (arrayIndex !== null) {
    return resolveArrayReference(objectName, objectKey, arrayIndex, objectVariables, arrayVariables, parseObjectValue)
  } else if (!objectKey) {
    return undefined
  } else {
    // Continue exact and normalized object-key lookup.
  }

  // Return resolved object key value when available.
  return getObjectVariableValue(objectVariables, objectName, objectKey)
}

// Resolve array item access with optional object-key lookup.
function resolveArrayReference(
  arrayName: string,
  objectKey: string | null,
  arrayIndex: number,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  parseObjectValue: ParseObjectValue,
): string | undefined {
  const arrayItem = arrayVariables[arrayName]?.[arrayIndex]
  // Return undefined when the requested array item is missing.
  if (typeof arrayItem !== 'string') {
    return undefined
  } else if (!objectKey) {
    // Return scalar array item access like `var(--sizes[0])`.
    return arrayItem
  } else if (!isObjectLikeArrayItem(arrayItem)) {
    // Keep unresolved when a keyed lookup targets a scalar array item.
    return undefined
  } else {
    // Parse object-shaped array items for `var(--items[0].key)`.
    const parsed = parseObjectValue(arrayItem, objectVariables)
    return getObjectVariableValue({ '--item': parsed }, '--item', objectKey)
  }
}

// Resolve one level deeper when a resolved value still contains var() access.
function resolveNestedReferenceValue(
  value: string | undefined,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  parseObjectValue: ParseObjectValue,
  depth: number,
): string | undefined {
  // Bound nested reference expansion to avoid cyclic object references.
  if (!value || !value.includes('var(') || depth >= 4) {
    return value
  } else {
    // Re-scan resolved value so object values can point at array items.
    return replaceObjectValueReferences(value, objectVariables, arrayVariables, parseObjectValue, depth + 1)
  }
}

// Detect inline object syntax without importing the object parser entry.
function isObjectLikeArrayItem(value: string): boolean {
  const trimmed = value.trim()
  // Return true when value is wrapped with `{ ... }`.
  return trimmed.startsWith('{') && trimmed.endsWith('}')
}
